import { Typography, Grid, Checkbox, FormControlLabel, Button } from '@mui/material';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store';
import { distributeDocuments } from 'thunks/productDocumentsThunk';
import DocumentDistributionLanguageSelect from './uploadDocument/uploadContractButtonAndDialog/DocumentDistributionLanguageSelect';
import { jbColors } from 'theme/jbColors';
import { DocumentV2 } from 'pco-product-service-internal-consumer';

export type PrintOrDistributeDocumentsProps = {
  documents: DocumentV2[];
  closeAction: () => void;
  taskId: string;
  isReadOnly: boolean;
};

const PrintOrDistributeDocuments = ({
  documents,
  closeAction,
  taskId,
  isReadOnly,
}: PrintOrDistributeDocumentsProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const CLM_URL = useSelector((state: RootState) => state.appConfig.CLM_URL);
  const initialChecked = (docs: typeof documents) => (docs ?? []).map((doc) => doc.documentUid ?? '').filter(Boolean);
  const [checked, setChecked] = React.useState<string[]>(() => initialChecked(documents));
  const [prevDocuments, setPrevDocuments] = React.useState(documents);
  if (documents !== prevDocuments) {
    setPrevDocuments(documents);
    if (documents?.length > 0) {
      setChecked(initialChecked(documents));
    }
  }
  const { selectedServiceAgreement } = useSelector((state: RootState) => state.serviceAgreements);
  const [language, setLanguage] = React.useState<string>('EN');
  const [languages, setLanguages] = React.useState<string[]>(['EN']);

  useEffect(() => {
    function handleLanguages() {
      if (documents.length === 0) {
        return;
      }

      const uniqueLanguages = [
        ...new Set(
          documents
            ?.filter((doc) => doc.documentUid && checked.includes(doc.documentUid))
            ?.map((doc) => doc.documentLanguages || []),
        ),
      ];

      if (uniqueLanguages.length === 0) {
        return;
      }

      if (uniqueLanguages.length === 1) {
        setLanguages(uniqueLanguages.flatMap((languages) => languages));
        return;
      }

      setLanguages(uniqueLanguages.reduce((prev, current) => prev.filter((lang) => current.includes(lang))));
    }

    handleLanguages();
  }, [checked, documents]);

  const handleCheckBox = (ev: React.ChangeEvent<HTMLInputElement>) => {
    const item = ev.currentTarget.value;
    if (checked.includes(item)) {
      setChecked(checked.filter((c) => c !== item));
    } else {
      const temp = [...checked];
      temp.push(item);
      setChecked(temp);
    }
  };

  const handleDistribute = () => {
    if (!selectedServiceAgreement?.serviceAgreementId) return;

    dispatch(distributeDocuments(taskId, language, checked, CLM_URL, selectedServiceAgreement.serviceAgreementId));
    closeAction();
  };

  const renderListOfDistributedDocuments = () => {
    return (
      <Grid container sx={{ mt: 1, mb: 0.5 }}>
        <Typography variant="h4" sx={{ color: jbColors.core.reflexBlue[100], mb: 2 }}>
          Distributed Documents
        </Typography>
        {documents.map((document) => (
          <Grid key={document.documentName} item xs={12} sx={{ ml: 1, mt: 1, mb: 0.5 }}>
            <Typography variant="body2" sx={{ color: jbColors.core.reflexBlue[100] }}>
              {document.documentName}
            </Typography>
          </Grid>
        ))}
      </Grid>
    );
  };

  return !isReadOnly ? (
    <Grid container data-testid="print-or-distribute-documents">
      <Grid item xs={12} sx={{ mt: 1, mb: 0.5 }}>
        Relevant Documents
      </Grid>
      <Grid item container xs={12} sx={{ mt: 1, mb: 1, display: 'flex', flexDirection: 'column' }}>
        {documents?.map((value, index) => {
          return (
            <FormControlLabel
              data-testid={`print-or-distribute-documents-checkbox-label-${index}`}
              key={value.documentUid}
              label={value.documentName}
              control={
                <Checkbox
                  data-testid={`print-or-distribute-documents-checkbox-${index}`}
                  value={value.documentUid}
                  checked={value.documentUid ? checked.includes(value.documentUid) : false}
                  onChange={(ev) => handleCheckBox(ev)}
                />
              }
            />
          );
        })}
      </Grid>
      <Grid container sx={{ mr: 1.2, borderRadius: 0, mt: 1, mb: 0.5 }}>
        <DocumentDistributionLanguageSelect language={language} languages={languages} setLanguage={setLanguage} />
      </Grid>
      <Grid container item xs={12} sx={{ display: 'flex', mt: 4 }}>
        <Grid
          item
          xs={6}
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            whiteSpace: 'nowrap',
          }}
        >
          <Button
            id="print-or-distribute-documents-later-button"
            sx={{
              backgroundColor: 'white',
              color: jbColors.core.reflexBlue[100],
              ':hover': {
                backgroundColor: jbColors.core.reflexBlue[100],
                color: 'white',
              },
            }}
            onClick={closeAction}
          >
            Distribute later
          </Button>
        </Grid>
        <Grid
          item
          xs={6}
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            whiteSpace: 'nowrap',
          }}
        >
          <Button
            id="distribute-documents-button"
            data-testid={`print-or-distribute-documents-button`}
            sx={{
              backgroundColor: jbColors.core.reflexBlue[100],
              color: 'white',
              '&:hover': {
                backgroundColor: 'white',
                color: jbColors.core.reflexBlue[100],
              },
            }}
            onClick={handleDistribute}
            disabled={checked.length === 0 && documents.length > 0 && !language}
          >
            Distribute documents
          </Button>
        </Grid>
      </Grid>
    </Grid>
  ) : (
    renderListOfDistributedDocuments()
  );
};

export default PrintOrDistributeDocuments;
